import React, { useState, useEffect, useCallback } from 'react';
import { bookingAPI, vehicleAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';

const SERVICE_TYPES = [
  { value: 'oil-change', label: 'Oil Change' },
  { value: 'tire-rotation', label: 'Tire Rotation' },
  { value: 'brake-service', label: 'Brake Service' },
  { value: 'engine-diagnostic', label: 'Engine Diagnostic' },
  { value: 'ac-service', label: 'AC Service' },
  { value: 'battery-replacement', label: 'Battery Replacement' },
  { value: 'full-service', label: 'Full Service' },
  { value: 'custom', label: 'Custom / Other' },
];

const TIME_SLOTS = [
  '09:00 AM','09:30 AM','10:00 AM','10:30 AM','11:00 AM','11:30 AM',
  '12:00 PM','12:30 PM','01:00 PM','01:30 PM','02:00 PM','02:30 PM',
  '03:00 PM','03:30 PM','04:00 PM','04:30 PM','05:00 PM','05:30 PM',
];

const STATUS_BADGE = {
  pending: 'badge-amber', confirmed: 'badge-blue',
  'in-progress': 'badge-cyan', completed: 'badge-green', cancelled: 'badge-red',
};

const STATUS_FLOW = {
  pending: ['confirmed', 'cancelled'],
  confirmed: ['in-progress', 'cancelled'],
  'in-progress': ['completed'],
  completed: [], cancelled: [],
};

const emptyForm = {
  vehicle: '', serviceType: 'oil-change', serviceDate: '', timeSlot: '',
  description: '', estimatedCost: '',
};

export default function BookingPage() {
  const { user, isStaff } = useAuth();
  const [bookings, setBookings] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [takenSlots, setTakenSlots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [bRes, vRes] = await Promise.all([
        bookingAPI.getAll(),
        vehicleAPI.getAll(),
      ]);
      setBookings(bRes.data.data || []);
      setVehicles(vRes.data.data || []);
    } catch { toast.error('Failed to load bookings'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Fetch taken slots when date changes
  useEffect(() => {
    if (!form.serviceDate) { setTakenSlots([]); return; }
    bookingAPI.getSlots(form.serviceDate)
      .then(r => setTakenSlots(r.data.takenSlots || []))
      .catch(() => {});
  }, [form.serviceDate]);

  const set = (f) => (e) => setForm(p => ({ ...p, [f]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.vehicle || !form.serviceDate || !form.timeSlot) {
      toast.error('Please fill all required fields'); return;
    }
    setSaving(true);
    try {
      await bookingAPI.create(form);
      toast.success('Booking created!');
      setShowModal(false);
      setForm(emptyForm);
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to create booking');
    } finally { setSaving(false); }
  };

  const updateStatus = async (id, status) => {
    try {
      await bookingAPI.updateStatus(id, status);
      toast.success(`Status updated to ${status}`);
      load();
    } catch { toast.error('Failed to update status'); }
  };

  const fmtDate = (d) => new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });

  const filtered = bookings.filter(b => {
    const matchStatus = filter === 'all' || b.status === filter;
    const q = search.toLowerCase();
    const matchSearch = !q || b.bookingNumber?.toLowerCase().includes(q)
      || b.customer?.name?.toLowerCase().includes(q)
      || b.vehicle?.make?.toLowerCase().includes(q);
    return matchStatus && matchSearch;
  });

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <div>
          <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 20, fontWeight: 700 }}>Bookings</h2>
          <p style={{ color: 'var(--text-muted)', fontSize: 13, marginTop: 2 }}>{filtered.length} appointments</p>
        </div>
        <button className="btn btn-primary" onClick={() => setShowModal(true)}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
          New Booking
        </button>
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
        <div className="search-bar" style={{ flex: 1, minWidth: 200 }}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
          <input placeholder="Search bookings…" value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        <div className="tabs" style={{ margin: 0, flex: 'none' }}>
          {['all','pending','confirmed','in-progress','completed','cancelled'].map(s => (
            <button key={s} className={`tab-btn ${filter === s ? 'active' : ''}`} onClick={() => setFilter(s)}
              style={{ textTransform: 'capitalize', padding: '7px 12px', fontSize: 12 }}>
              {s === 'all' ? 'All' : s}
            </button>
          ))}
        </div>
      </div>

      {/* Table */}
      {loading ? (
        <div className="loading-screen" style={{ height: 300 }}><div className="spinner" /></div>
      ) : filtered.length === 0 ? (
        <div className="card"><div className="empty-state">
          <div className="empty-state-icon">📅</div>
          <div className="empty-state-title">No bookings found</div>
          <div className="empty-state-text">Try adjusting your filters or create a new booking.</div>
        </div></div>
      ) : (
        <div className="table-wrapper">
          <table>
            <thead><tr>
              <th>Booking #</th>
              {isStaff && <th>Customer</th>}
              <th>Vehicle</th>
              <th>Service</th>
              <th>Date</th>
              <th>Slot</th>
              <th>Status</th>
              {isStaff && <th>Actions</th>}
            </tr></thead>
            <tbody>
              {filtered.map(b => (
                <tr key={b._id}>
                  <td style={{ fontWeight: 600, color: 'var(--accent)' }}>{b.bookingNumber}</td>
                  {isStaff && <td>{b.customer?.name || '—'}</td>}
                  <td>{b.vehicle ? `${b.vehicle.make} ${b.vehicle.model} (${b.vehicle.licensePlate})` : '—'}</td>
                  <td style={{ textTransform: 'capitalize' }}>{b.serviceType?.replace(/-/g, ' ')}</td>
                  <td>{fmtDate(b.serviceDate)}</td>
                  <td>{b.timeSlot}</td>
                  <td><span className={`badge ${STATUS_BADGE[b.status] || 'badge-gray'}`}>{b.status}</span></td>
                  {isStaff && (
                    <td>
                      <div style={{ display: 'flex', gap: 6 }}>
                        {(STATUS_FLOW[b.status] || []).map(next => (
                          <button key={next} className="btn btn-ghost btn-sm"
                            style={{ fontSize: 11, padding: '4px 8px', textTransform: 'capitalize' }}
                            onClick={() => updateStatus(b._id, next)}>
                            → {next}
                          </button>
                        ))}
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* New Booking Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setShowModal(false)}>
          <div className="modal">
            <div className="modal-header">
              <span className="modal-title">New Booking</span>
              <button className="modal-close" onClick={() => setShowModal(false)}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="20" height="20"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                <div className="form-group">
                  <label className="form-label">Vehicle *</label>
                  <select className="form-select" value={form.vehicle} onChange={set('vehicle')} required>
                    <option value="">Select a vehicle</option>
                    {vehicles.map(v => (
                      <option key={v._id} value={v._id}>{v.make} {v.model} — {v.licensePlate}</option>
                    ))}
                  </select>
                  {vehicles.length === 0 && (
                    <p className="form-hint">No vehicles found. <a href="/vehicles" style={{ color: 'var(--accent)' }}>Add one first.</a></p>
                  )}
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Service Type *</label>
                    <select className="form-select" value={form.serviceType} onChange={set('serviceType')}>
                      {SERVICE_TYPES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Estimated Cost (₹)</label>
                    <input type="number" className="form-input" placeholder="0" value={form.estimatedCost} onChange={set('estimatedCost')} />
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label">Service Date *</label>
                  <input type="date" className="form-input" value={form.serviceDate} onChange={set('serviceDate')}
                    min={new Date().toISOString().split('T')[0]} required />
                </div>

                {form.serviceDate && (
                  <div className="form-group">
                    <label className="form-label">Time Slot *</label>
                    <div className="slot-grid">
                      {TIME_SLOTS.map(slot => (
                        <button key={slot} type="button"
                          className={`slot-btn ${takenSlots.includes(slot) ? 'slot-taken' : ''} ${form.timeSlot === slot ? 'slot-selected' : ''}`}
                          disabled={takenSlots.includes(slot)}
                          onClick={() => setForm(p => ({ ...p, timeSlot: slot }))}>
                          {slot}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                <div className="form-group">
                  <label className="form-label">Description / Notes</label>
                  <textarea className="form-textarea" placeholder="Describe the issue or service needed…"
                    value={form.description} onChange={set('description')} />
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-ghost" onClick={() => setShowModal(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={saving}>
                  {saving ? 'Saving…' : 'Create Booking'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}